// Display helpers for the one score NetRiskScan publishes: the NetRiskScan Index — 0-100, higher =
// cleaner. Replaces the old risk-direction "Risk Score" (higher = riskier); the whole point of this
// module is that the direction flip happens in resolveNetRiskScanIndex, never per-page.
//
// What is deliberately NOT here: any numeric band thresholds. NetRiskScanIndexClassifier owns that
// mapping and every fresh result carries indexBand. Older snapshots may fall back to their stored
// netRiskScanLevel label, but the frontend never turns a number into a band.
import type { AddressType, PublicRisk } from "../types/dto";
import type { getStrings } from "../i18n/strings";

type Strings = ReturnType<typeof getStrings>;

/// Orders the five NetRiskScanLevels bands so two reports can be compared — higher rank = cleaner,
/// the opposite ordering from the old risk-direction RiskLevelRank this replaced. Shared rather than
/// duplicated per comparison module: with a `?? fallback` lookup a band missing from the map does not
/// fail loudly, it silently ranks as something else.
const IndexBandRank: Record<string, number> = {
  high_risk: 0,
  poor: 1,
  fair: 2,
  good: 3,
  excellent: 4,
  unknown: 2,
};

export function indexBandRank(band: string | null | undefined): number {
  return (band == null ? undefined : IndexBandRank[band]) ?? IndexBandRank.unknown;
}

function clampIndex(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

/// The one score to read off a result, live or historical. A fresh query result always has
/// netRiskScanIndex populated by the server and this just returns it unchanged. Only a pre-migration
/// snapshot — one that has riskScore but not netRiskScanIndex — falls through to the legacy inversion
/// (spec: "don't break old permalinks"), and that inversion happens here and only here.
export function resolveNetRiskScanIndex(risk: PublicRisk): number | null {
  if (risk.netRiskScanIndex !== null && risk.netRiskScanIndex !== undefined) {
    return risk.netRiskScanIndex;
  }
  if (typeof risk.riskScore === "number") {
    return clampIndex(100 - risk.riskScore);
  }
  return null;
}

/// The numeric band to color/label a result by. A live result always uses the backend's indexBand.
/// netRiskScanLevel is consulted only for a pre-indexBand snapshot and remains eligibility-aware.
export function resolveIndexBand(risk: PublicRisk): string | null {
  if (risk.indexBand) {
    return risk.indexBand;
  }
  if (risk.netRiskScanLevel) {
    return risk.netRiskScanLevel;
  }
  return null;
}

/// The one figure as the product specifies it everywhere it appears: a bare "94", or "—" when the
/// engine had insufficient data. Never "94%" (reads as "94% risk", the opposite of what a HIGH index
/// means) and never "94/100" — a fraction reads as a score out of a total, which is the framing the
/// second-score problem kept coming back through.
export function formatNetRiskScanIndexValue(risk: PublicRisk): string {
  const index = resolveNetRiskScanIndex(risk);
  return index === null ? "—" : `${index}`;
}

/// The one line of secondary text under the index figure: how much data this verdict rests on.
///
/// Explicitly a sentence about the data, not a rating. It is built as text rather than exposed as a
/// number to a meter component so there is nothing for a future layout to promote into a second
/// score — coverage and the index are decoupled by spec (§6) and low coverage must never read as
/// "worse", only as "we found less".
export function formatCoverageLine(risk: PublicRisk, one: string, many: string): string {
  const template = risk.activeProviderCount === 1 ? one : many;
  return template
    .replace("{coverage}", String(risk.coveragePercent))
    .replace("{count}", String(risk.activeProviderCount));
}

/// Coarsens the 10-value AddressType into the 3-word vocabulary the top badge/NotApplicable pill uses
/// (spec §12: "Local IP / Private IP / Reserved IP" — never the more technical addressTypeNames label,
/// which is reserved for the Identity panel's "Address type" field).
function addressTypeBadgeBucket(addressType: AddressType): "loopback" | "private" | "reserved" {
  if (addressType === "loopback") return "loopback";
  if (addressType === "private" || addressType === "carrier_grade_nat") return "private";
  return "reserved";
}

/// The top-right verdict pill and ScoreRing footer label, for every scoreStatus — the one place that
/// decides when it's safe to show indexBandLabel (Good/Excellent/...) at all. A "success" result
/// gets its real level label; "insufficient_data" and "not_applicable" each get their own vocabulary
/// (spec §12: never let a special-purpose or under-evidenced address show a normal risk grade).
export function resolveNetRiskScanBadgeLabel(
  scoreStatus: string,
  addressType: AddressType,
  indexBandLabel: string,
  strings: Strings,
): string {
  if (scoreStatus === "not_applicable") {
    switch (addressTypeBadgeBucket(addressType)) {
      case "loopback":
        return strings.netRiskScanBadgeLocalIp ?? "Local IP";
      case "private":
        return strings.netRiskScanBadgePrivateIp ?? "Private IP";
      default:
        return strings.netRiskScanBadgeReservedIp ?? "Reserved IP";
    }
  }
  if (scoreStatus === "insufficient_data") {
    return strings.netRiskScanBadgeUnavailable ?? "Unavailable";
  }
  return indexBandLabel;
}
